import ImgixClient from '@imgix/js-core';
import {
  centerCropUrl as cdaasCenterCropUrl,
  getAutoFormatUrl as cdaasGetAutoFormatUrl,
  hasCdaasUrl,
} from './cdaas';
import { consumer } from '@tock/coretypes';
import { logLegacyImageUrl } from './legacyImageLogger';

// Using an empty src attribute on an image tag in html can be dangerous because it causes the
// browser to issue another request for the current page. This can cause a thundering herd problem.
// Instead, in the unusual situation where we can't generate a valid image url, we fall back to
// a 1x1 transparent gif as a data url.
const PLACEHOLDER_GIF = 'data:image/gif;base64,R0lGODlhAQABAID/AMDAwAAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

const LEGACY_IMAGE_CDN_DOMAIN = 'lh3.googleusercontent.com';
export const IMGIX_DOMAIN = 'tock-public.imgix.net';
const PUBLIC_ASSETS_BUCKET_URL = 'https://storage.googleapis.com/tock-public-assets';

export const DPPX_SIZES = [2, 3];

export interface SizeOptions {
  width: number;
  height: number;
}

export interface ImageOptions {
  width?: number;
  height?: number;
  /**
   * 0-100, defaults to 45 with auto=compress
   */
  q?: number;
}

const client = new ImgixClient({
  domain: IMGIX_DOMAIN,
});

export function getRelativeCloudStoragePath(url: string) {
  return url.split(PUBLIC_ASSETS_BUCKET_URL)[1];
}

export function getRelativeImgixUrl(url: string) {
  return url.split(`https://${IMGIX_DOMAIN}`)[1];
}

export function getImgixBasePath(url: string): string {
  const relativePath = getRelativeImgixUrl(url);
  const questionMarkIndex = relativePath.indexOf('?');
  return questionMarkIndex > 0 ? relativePath.substring(0, questionMarkIndex) : relativePath;
}

export function hasImgIxUrl(url?: string) {
  if (!url) {
    return false;
  }
  return url.includes(IMGIX_DOMAIN);
}

export function hasLegacyCdnUrl(url?: string) {
  if (!url) {
    return false;
  }
  return url.includes(LEGACY_IMAGE_CDN_DOMAIN);
}

export function isUrlEncoded(url: string): boolean {
  return url !== decodeURI(url);
}

/**
 * This does some checks for legacy and existing imgix urls, because for some components the
 * url is entered in clowncar cms. so this guards against that a bit, but long term not sure
 * just yet what we want to do with that
 */
export function getAutoFormatUrl(url?: string, options?: ImageOptions) {
  if (!url) {
    return PLACEHOLDER_GIF;
  }
  if (hasCdaasUrl(url)) {
    return cdaasGetAutoFormatUrl(url, options);
  }
  if (hasLegacyCdnUrl(url)) {
    logLegacyImageUrl('getAutoFormatUrl', url);
    return url;
  }
  const isImgixUrl = hasImgIxUrl(url);
  const src = isImgixUrl ? getImgixBasePath(url) : getRelativeCloudStoragePath(url);
  return client.buildURL(
    src,
    { ...options, auto: 'format' },
    { disablePathEncoding: isUrlEncoded(url) }
  );
}

function getAutoFormatSrcSetForWidthPx(width: number, url?: string) {
  if (!url) {
    return PLACEHOLDER_GIF;
  }

  return DPPX_SIZES.map(
    (size) => `${getAutoFormatUrl(url, { width: width * size })} ${size}x`
  ).join(', ');
}

export function getAutoFormatSrcAndSrcSetForWidthPx(
  { width }: { width: number },
  url?: string
): { src: string; srcSet: string } {
  if (!url) {
    return { src: PLACEHOLDER_GIF, srcSet: PLACEHOLDER_GIF };
  }

  return {
    src: getAutoFormatUrl(url, { width }),
    srcSet: getAutoFormatSrcSetForWidthPx(width, url),
  };
}

export function centerCropThumbnailUrl(imageUrl: string | undefined) {
  if (!imageUrl) {
    return PLACEHOLDER_GIF;
  }
  return centerCropUrl(imageUrl, {
    width: 576,
    height: 384,
    q: 35,
  });
}

export function centerCropUrl(url?: string, options?: ImageOptions) {
  if (!url) {
    return PLACEHOLDER_GIF;
  }

  if (hasCdaasUrl(url)) {
    return cdaasCenterCropUrl(url, options);
  }

  if (hasLegacyCdnUrl(url)) {
    logLegacyImageUrl('centerCropUrl', url);
  }

  const isImgixUrl = hasImgIxUrl(url);
  const src = isImgixUrl ? getImgixBasePath(url) : getRelativeCloudStoragePath(url);

  const defaultOptions = {
    auto: 'format',
    fit: 'crop',
    crop: 'focalpoint',
  };
  return client.buildURL(
    src,
    {
      ...defaultOptions,
      h: options?.height,
      w: options?.width,
      q: options?.q,
    },
    { disablePathEncoding: isUrlEncoded(url) }
  );
}

export const getBusinessThumbnailSrc = (
  business: consumer.IBusinessConfig | consumer.OfferingBusiness,
  options: SizeOptions = { width: 120, height: 120 }
): string => {
  if (!business) {
    return PLACEHOLDER_GIF;
  }
  const [firstProfileImage] = (business.profileImage ?? []).map((image : any) => image.imageBackingUrl);
  return firstProfileImage
    ? centerCropUrl(firstProfileImage, {
        width: options.width,
        height: options.height,
      })
    : PLACEHOLDER_GIF;
};

export default client;
